import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Loader2, Image as ImageIcon, X, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { storeApi } from './storeApi';
import StoreLayout from './StoreLayout';

const STATUS_META = {
  pending:  { label: 'Pending Review', cls: 'bg-amber-100 text-amber-800',  icon: Clock },
  approved: { label: 'Approved',       cls: 'bg-green-100 text-green-800',  icon: CheckCircle2 },
  rejected: { label: 'Rejected',       cls: 'bg-red-100 text-red-800',      icon: XCircle },
};

function absImg(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads/')) return `/api${url}`;
  return url;
}

export default function StoreProductRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [productName, setProductName] = useState('');
  const [mrp, setMrp] = useState('');
  const [gramage, setGramage] = useState('');
  const [requestedPrice, setRequestedPrice] = useState('');
  const [file, setFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInput = useRef(null);

  function load() {
    setLoading(true);
    storeApi.get('/store/product-requests')
      .then((r) => setItems(r.data.items || []))
      .catch(() => toast.error('Failed to load requests'))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  function resetForm() {
    setProductName(''); setMrp(''); setGramage(''); setRequestedPrice('');
    setFile(null); setImagePreview(null);
    if (fileInput.current) fileInput.current.value = '';
  }

  function onPickFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    setFile(f);
    setImagePreview(URL.createObjectURL(f));
  }

  async function submit(e) {
    e.preventDefault();
    if (!productName.trim()) return toast.error('Product name is required');
    if (!gramage.trim()) return toast.error('Gramage is required');
    if (!mrp || Number(mrp) <= 0) return toast.error('MRP must be greater than 0');

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('product_name', productName.trim());
      fd.append('mrp', String(Number(mrp)));
      fd.append('gramage', gramage.trim());
      if (requestedPrice) fd.append('requested_price', String(Number(requestedPrice)));
      if (file) fd.append('file', file);
      const r = await storeApi.post('/store/product-requests', fd);
      toast.success('Request submitted — admin will review shortly');
      setItems((prev) => [r.data.request, ...prev]);
      resetForm();
      setShowForm(false);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Submission failed';
      toast.error(typeof msg === 'string' ? msg : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <StoreLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="store-headline text-3xl">Request New Product</h1>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="store-btn-citrus text-sm"
                  data-testid="open-request-form">
            <Plus className="w-4 h-4 inline mr-1" /> New Request
          </button>
        )}
      </div>

      <p className="text-sm text-slate-500 mb-4 max-w-2xl">
        Suggest a product you&apos;d like to stock. Once you submit, the OLTOWNS admin team will review
        and add it to the catalogue if approved. You can track the status of each request below.
      </p>

      {showForm && (
        <form onSubmit={submit} className="store-card p-5 mb-6 space-y-4" data-testid="request-form">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-orange-900">New Product Details</h2>
            <button type="button" onClick={() => { resetForm(); setShowForm(false); }}
                    className="text-slate-400 hover:text-slate-600"
                    data-testid="close-request-form">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">Product Name <span className="text-red-500">*</span></span>
              <input type="text" value={productName} onChange={(e) => setProductName(e.target.value)}
                     className="store-input mt-1" placeholder="e.g. Aashirvaad Atta 5kg"
                     required data-testid="req-product-name" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">Gramage / Pack Size <span className="text-red-500">*</span></span>
              <input type="text" value={gramage} onChange={(e) => setGramage(e.target.value)}
                     className="store-input mt-1" placeholder="e.g. 500 g, 1 kg, 750 ml, 12 pcs"
                     required data-testid="req-gramage" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">MRP (₹) <span className="text-red-500">*</span></span>
              <input type="number" step="0.01" min="0" value={mrp} onChange={(e) => setMrp(e.target.value)}
                     className="store-input mt-1" placeholder="e.g. 250.00"
                     required data-testid="req-mrp" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">Requested Price (₹) <span className="text-slate-400 font-normal">(optional)</span></span>
              <input type="number" step="0.01" min="0" value={requestedPrice} onChange={(e) => setRequestedPrice(e.target.value)}
                     className="store-input mt-1" placeholder="What you'd like to pay"
                     data-testid="req-price" />
            </label>
          </div>

          <div>
            <span className="text-xs font-semibold text-slate-700">Product Image <span className="text-slate-400 font-normal">(optional, max 5 MB)</span></span>
            <div className="mt-1 flex items-center gap-3">
              <button type="button" onClick={() => fileInput.current?.click()}
                      className="border border-orange-300 rounded-md px-3 py-2 text-sm text-orange-700 hover:bg-orange-50"
                      data-testid="req-pick-image">
                <ImageIcon className="w-4 h-4 inline mr-1" /> {file ? 'Change image' : 'Upload image'}
              </button>
              <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={onPickFile}
                     data-testid="req-file-input" />
              {imagePreview && (
                <img src={imagePreview} alt="preview"
                     className="w-16 h-16 object-cover rounded-md border border-slate-200" />
              )}
            </div>
          </div>

          <div className="pt-3 border-t border-slate-200 flex justify-end gap-2">
            <button type="button" onClick={() => { resetForm(); setShowForm(false); }}
                    className="px-4 py-2 rounded-full text-sm text-slate-600 hover:bg-slate-100"
                    data-testid="req-cancel">
              Cancel
            </button>
            <button type="submit" disabled={submitting} className="store-btn-citrus text-sm disabled:opacity-60"
                    data-testid="req-submit">
              {submitting ? <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> : <Plus className="w-4 h-4 inline mr-1" />}
              {submitting ? 'Submitting…' : 'Submit Request'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-20 text-orange-700">Loading…</div>
      ) : items.length === 0 ? (
        <div className="store-card p-12 text-center text-slate-400">
          <ImageIcon className="w-10 h-10 mx-auto mb-3 text-orange-700" />
          No requests yet. Click <b>New Request</b> above to suggest a product.
        </div>
      ) : (
        <div className="space-y-3" data-testid="requests-list">
          {items.map((r) => {
            const m = STATUS_META[r.status] || STATUS_META.pending;
            const Icon = m.icon;
            return (
              <div key={r.id} className="store-card p-4 flex items-center gap-4"
                   data-testid={`request-${r.id}`}>
                <div className="w-16 h-16 rounded-lg bg-slate-50 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                  {r.image_url ? (
                    <img src={absImg(r.image_url)} alt={r.product_name} className="w-full h-full object-cover"
                         onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-slate-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-orange-950 truncate">{r.product_name}</p>
                  <p className="text-xs text-slate-500">
                    {r.gramage} · MRP ₹{Number(r.mrp).toLocaleString('en-IN')}
                    {r.requested_price != null && <> · Requested ₹{Number(r.requested_price).toLocaleString('en-IN')}</>}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-1">
                    Submitted {(r.created_at || '').split('T')[0]}
                    {r.reviewed_by_name && <> · Reviewed by {r.reviewed_by_name}</>}
                  </p>
                  {r.admin_notes && (
                    <p className="text-xs text-slate-600 mt-1 italic">&quot;{r.admin_notes}&quot;</p>
                  )}
                </div>
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${m.cls}`}>
                  <Icon className="w-3.5 h-3.5" /> {m.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </StoreLayout>
  );
}
